import React, { SetStateAction, useState, useRef } from "react";
import { DateTime } from "luxon";
import { amountFormatter } from "services";
import { ChevronUp, ChevronDown, EllipsisVertical } from "lucide-react";
import { Button } from "../../../../../shadcn/components/ui/button";
import { useOutsideClicker } from "@locoworks/reusejs-toolkit-react-hooks";
interface EventContentDataProps {
  currentUserLatestBid?: {
    user_uuid: string;
    bidding_amount: number;
  };
  latestBid?: {
    user_uuid: string;
    number_of_extension?: number;
  };
  latestBidAmount?: string | number;
  biddingType: string;
  data: {
    bidding_type: string;
    reserve_price: string;
    bidding_increment_amount: string;
    extend_time_at?: string;
  };
  status: string;
  endTime?: number;
  app?: string;
  showModal?: boolean;
  setShowModal?: React.Dispatch<SetStateAction<boolean>>;
}
export default function EventContentData({
  currentUserLatestBid,
  latestBid,
  latestBidAmount,
  data,
  status,
  endTime,
  app,
  biddingType,
  showModal,
  setShowModal,
}: EventContentDataProps) {
  const formattedEndTime = endTime
    ? `${DateTime.fromMillis(endTime)
        .setZone("Asia/Kolkata")
        .toFormat("hh:mm:ss a")} (IST)`
    : "00";

  const renderLatestBid = () => {
    if (!currentUserLatestBid) return "NA";
    const isCurrentUserLeading =
      currentUserLatestBid.user_uuid === latestBid?.user_uuid;

    return (
      <span
        className={`font-bold ${
          isCurrentUserLeading ? "text-green-500" : "text-red-500"
        }`}
      >
        ₹ {amountFormatter(currentUserLatestBid.bidding_amount)}
        {/* {isCurrentUserLeading && " (Rank: 1)"} */}
      </span>
    );
  };

  const calculateNextBid = () => {
    if (!latestBidAmount) return "₹ 00";
    const latestBidValue =
      typeof latestBidAmount === "string"
        ? parseInt(latestBidAmount)
        : latestBidAmount;
    let nextBid;
    if (biddingType === "rp") {
      if (!latestBid && latestBidValue === parseInt(data.reserve_price)) {
        nextBid = latestBidValue;
      } else {
        nextBid = latestBidValue + parseInt(data.bidding_increment_amount);
      }
    } else {
      nextBid = latestBidValue + parseInt(data.bidding_increment_amount);
    }

    return `₹ ${amountFormatter(nextBid)} or above (multiples of increment)`;
  };

  const [showExtended, setShowExtended] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const ellipsisRef = useRef<HTMLDivElement>(null);

  const outsideClickRef = useOutsideClicker(() => {
    const handleClick = (event: MouseEvent) => {
      if (ellipsisRef.current?.contains(event.target as Node)) {
        return;
      }
      setShowButton(false);
    };

    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  });

  return (
    <div className="text-xs px-[10px] font-sans text-brand-textBlack">
      <div className="pt-4 pb-5 flex justify-between items-center">
        <dt className="text-sm font-semibold text-brand-textBlack">
          Event Details
        </dt>

        {app == "banker" && (
          <div className="relative">
            {status === "cancelled" ? (
              <Button
                disabled={true}
                variant="outline"
                className={`text-xs px-6 w-[125px] rounded-[12px]  font-semibold ${"bg-red-700 text-white"}  hover:bg-red-700 hover:text-white`}
              >
                Event Cancelled
              </Button>
            ) : (
              <div
                ref={ellipsisRef}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowButton(!showButton);
                }}
              >
                <EllipsisVertical className="my-auto hover:cursor-pointer" />
              </div>
            )}

            {status !== "end" && (
              <div
                className={`bg-white p-2 rounded-xl absolute right-2 -bottom-14 shadow-md transition-all duration-200 ease-in-out ${
                  showButton
                    ? "opacity-100 translate-y-0 scale-100"
                    : "opacity-0 translate-y-[-10px] scale-95 pointer-events-none"
                }`}
              >
                <Button
                  onClick={() => {
                    if (setShowModal) setShowModal(!showModal);
                  }}
                  ref={outsideClickRef}
                  disabled={status === "cancelled"}
                  variant="outline"
                  className={`text-xs px-6 w-[125px] rounded-[12px] font-semibold ${
                    status === "cancelled"
                      ? "bg-red-700 text-white"
                      : "bg-white text-red-700 border-red-700"
                  } hover:bg-red-700 hover:text-white`}
                >
                  {status === "cancelled" ? "Event Cancelled" : "Cancel Event"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <dl className={`divide-y "divide-gray-300"}`}>
        <div className="py-4 grid grid-cols-2 gap-2">
          <dt className="text-xs text-brand-textLightGrey">
            {app === "banker" ? "Last Bid" : "Your Last Bid"}
          </dt>
          <dd className="">{renderLatestBid()}</dd>
        </div>

        <div className="py-3 grid grid-cols-2 gap-2">
          <dt className="text-xs  text-brand-textLightGrey">
            Next Possible Bid
          </dt>
          <dd className="text-xs">{`${calculateNextBid()}`}</dd>
        </div>
        <div className="py-4 grid grid-cols-2 gap-2">
          <dt className="text-xs  text-brand-textLightGrey">Reserve Price</dt>
          <dd className=" text-xs ">₹ {amountFormatter(data.reserve_price)}</dd>
        </div>
        <div className="py-4 grid grid-cols-2 gap-2">
          <dt className="text-xs  text-brand-textLightGrey">
            Bid Increment Amount
          </dt>
          <dd className=" text-xs ">
            ₹ {amountFormatter(data?.bidding_increment_amount)}
          </dd>
        </div>
        <div className="py-4 grid grid-cols-2 gap-2">
          <dt className="text-xs  text-brand-textLightGrey">
            {app === "banker"
              ? `Auction ${status === "end" ? "ended" : "ends"} at`
              : `${status === "end" ? "Ended" : "Ends"} at`}
          </dt>
          <dd className="">{formattedEndTime}</dd>
        </div>
        <div className="py-4 grid grid-cols-2 gap-2">
          <dt className="text-xs text-brand-textLightGrey">
            Number of Extensions
          </dt>
          <dd className="">{latestBid?.number_of_extension || "0"}</dd>
        </div>
        {app === "banker" && (
          <div>
            {showExtended && (
              <div className="py-4 grid grid-cols-2 gap-2">
                <dt className="text-xs  text-brand-textLightGrey">
                  Extended by
                </dt>
                <dd className="text-xs ">{data.extend_time_at} mins</dd>
              </div>
            )}
            <div className="text-center py-2">
              <button
                className="text-xs text-brand-textGrey underline cursor-pointer"
                onClick={() => setShowExtended(!showExtended)}
              >
                <p className="flex text-sm gap-1 items-center">
                  {showExtended ? "Show Less" : "Show More"}
                  <span>{showExtended ? <ChevronDown /> : <ChevronUp />}</span>
                </p>
              </button>
            </div>
          </div>
        )}
      </dl>
    </div>
  );
}
